import {
	Badge,
	Box,
	Button as MuiButton,
	IconButton,
	Paper,
	Popover,
	Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlockingRouter } from '../../../../hooks/useBlockingRouter';
import { useSelector } from 'react-redux';
import { Icon } from '../../../../blocks/icons';
import { useNewEventsSubscription } from '../../../../hooks/useNewEventsSubscription';
import { FlattenedUser } from '../../../../types/user';
import { Events } from '../../../Events';
import styles from './EventNotifications.module.scss';
import MuiModal from '../../../../mui/MuiModal/MuiModal';
import { gql } from '@apollo/client';
import { Button } from '../../../../blocks/Button';
import { useEventNotifications_DismissAllEventsMutation } from '#types';
import { useToast } from '../../../../hooks/useToast';
import { EventFeedListType } from '../../../eventFeed';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const dismissAllEventsMutation = gql`
	mutation EventNotifications_DismissAllEvents {
		dismissAllEvents
	}
`;

export const EventNotifications: React.FC = () => {
	const router = useBlockingRouter();
	const user: FlattenedUser = useSelector( ( state: any ) => state.user );
	const { showError, showSuccess } = useToast();
	const [ open, setOpen ] = useState( false );
	const [ showDismissModal, setShowDismissModal ] = useState( false );
	const ref = useRef<HTMLDivElement>( null );
	const [ dismissAllEvents, { loading } ] =
		useEventNotifications_DismissAllEventsMutation();

	const { badgeContent, reset } = useNewEventsSubscription();

	useEffect( () => {
		if ( open ) {
			reset();
		}
	}, [ open, reset ] );

	useEffect( () => {
		const closePopover = () => {
			setOpen( false );
		};
		router.events.on( 'routeChangeStart', closePopover );
		return () => {
			router.events.off( 'routeChangeStart', closePopover );
		};
	}, [ router.events ] );

	const closeOnCalendarMenuSelection = useCallback( () => {
		setOpen( false );
	}, [] );

	if ( !user?.isLoggedIn ) {
		return null;
	}

	const handleClick = async () => {
		const res = await dismissAllEvents();
		if ( res.errors ) {
			res.errors.map( ( error ) => showError( error.message ) );
		} else if ( res.data?.dismissAllEvents ) {
			showSuccess( 'Events dismissed' );
		}
		setShowDismissModal( false );
		setOpen( false );
	};

	return (
		<Box ref={ ref } className={ styles.container }>
			<IconButton
				onClick={ () => setOpen( true ) }
				className={ styles.iconButton }
				aria-label='Activity feed'
			>
				<Badge
					badgeContent={ badgeContent }
					color='secondary'
					className={ styles.badge }
					anchorOrigin={ { vertical: 'top', horizontal: 'left' } }
				>
					<Icon type='bell-off' className={ styles.icon } />
				</Badge>
			</IconButton>
			<Popover
				open={ open }
				onClose={ () => setOpen( false ) }
				anchorEl={ ref.current }
				anchorOrigin={ {
					vertical: 'bottom',
					horizontal: 'right',
				} }
				transformOrigin={ {
					vertical: 'top',
					horizontal: 'right',
				} }
				/* keepMounted is so we don't rerun queries in Events on
				every open, but has a potential hydration issue with SSR */
				keepMounted
				/* tells MUI's Modal layer never to touch body overflow so
				SSR won't sometimes set body overflow:hidden, preventing
				scrolling */
				disableScrollLock
			>
				<Paper className={ styles.paper } onClick={ () => setOpen( false ) }>
					<Typography variant='h6'>Activity Feed</Typography>
					<MuiButton
						onClick={ () => {
							setShowDismissModal( true );
							setOpen( false );
						} }
						fullWidth
						variant='text'
						color='secondary'
						startIcon={ <Icon type='trash' /> }
					>
						Dismiss all
					</MuiButton>
					<Events
						hideActionButtons
						open={ open }
						listType={ EventFeedListType.Dashboard }
						onCalendarMenuItemSelect={ closeOnCalendarMenuSelection }
					/>
				</Paper>
			</Popover>
			<MuiModal
				muiModalProps={ {
					open: showDismissModal,
					onClose: () => setShowDismissModal( false ),
				} }
				title='Dismiss All Events'
				description='Are you sure you want to dismiss all events? Certain events cannot be dismissed'
				controls={
					<Button
						onClick={ () => handleClick() }
						data-cy='dismiss-all-events-button'
						loading={ loading }
					>
						Yes
					</Button>
				}
			></MuiModal>
		</Box>
	);
};
